import { useState } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useBrandKitSources } from 'hooks/queries/useBrandKitSources'
import { useInfiniteBrandKitMediaFiles, IMediaFile } from 'hooks/queries/brandKits/useInfiniteBrandKitMediaFiles'
import { collectResults } from 'utils/query'
import { BrandKitSource, SourceMaterialsMediaObject } from 'types'
import { CampaignSource, CampaignSourceObject } from '../Types'
import { SourceMaterialsSection } from './SourceMaterialsSection'
import { MediaSection } from './MediaSection'
import Styles from './BrandKitContextModal.module.scss'
import cx from 'classnames'

interface BrandKitContextModalProps {
  brandKitId: number
  existingSources: CampaignSource[]
  onAdd: (source: CampaignSourceObject, mediaFileId?: number) => void
  mediaEnabled?: boolean
}

const SOURCE_TYPES = ['Website', 'PlainText', 'SourceMaterialsMediaObject', 'AlmanacFile'] as const

export const BrandKitContextModal = ({
  brandKitId,
  existingSources,
  onAdd,
  mediaEnabled = true,
}: BrandKitContextModalProps) => {
  const { closeModal } = useModals()
  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<number>>(new Set())
  const [selectedMediaFileIds, setSelectedMediaFileIds] = useState<Set<number>>(new Set())

  const sourcesQuery = useBrandKitSources({ brandKitId, sourceType: [...SOURCE_TYPES] })
  const brandKitSources = collectResults(sourcesQuery, (data) => data.brand_kit_sources)

  const mediaQuery = useInfiniteBrandKitMediaFiles({ brandKitId })
  const mediaFiles = collectResults(mediaQuery, (data) => data.media_files)

  const existingSourceIds = new Set(existingSources.map((s) => s.source.id as number))
  const existingMediaFileIds = new Set(
    existingSources.map((s) => s.mediaFileId).filter((id): id is number => id != null),
  )

  const toggleSource = (id: number) => {
    setSelectedSourceIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleMedia = (mediaFileId: number) => {
    setSelectedMediaFileIds((prev) => {
      const next = new Set(prev)
      if (next.has(mediaFileId)) next.delete(mediaFileId)
      else next.add(mediaFileId)
      return next
    })
  }

  const totalSelected = selectedSourceIds.size + selectedMediaFileIds.size

  const handleAdd = () => {
    brandKitSources
      .filter((s: BrandKitSource) => selectedSourceIds.has(s.source.id as number))
      .forEach((s: BrandKitSource) => onAdd(s.source as CampaignSourceObject))

    mediaFiles
      .filter((f) => selectedMediaFileIds.has(f.media_file_id))
      .forEach((file) => {
        const source: SourceMaterialsMediaObject = {
          id: file.media_file_id,
          type: 'SourceMaterialsMediaObject',
          source_url: file.source_url,
          mime_type: file.mime_type,
          created_at: (file as IMediaFile & { created_at: string }).created_at ?? '',
        }
        onAdd(source, file.media_file_id)
      })

    closeModal()
  }

  return (
    <Modal.Root size="md" onPressOutside={closeModal} aria-label="Add from Brand Kit">
      <Modal.Header title="Add from Brand Kit" />
      <Modal.Content className={Styles.content}>
        <section className={Styles.section}>
          <Heading level={4}>Source Materials</Heading>
          <SourceMaterialsSection
            isLoading={sourcesQuery.isLoading}
            brandKitSources={brandKitSources}
            existingSourceIds={existingSourceIds}
            selectedSourceIds={selectedSourceIds}
            onToggle={toggleSource}
          />
        </section>
        {mediaEnabled && (
          <section className={cx(Styles.section, Styles.mediaSection)}>
            <Heading level={4}>Media Library</Heading>
            <MediaSection
              isLoading={mediaQuery.isLoading}
              mediaFiles={mediaFiles}
              selectedMediaFileIds={selectedMediaFileIds}
              existingMediaFileIds={existingMediaFileIds}
              onToggle={toggleMedia}
            />
          </section>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" size="lg" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton variant="primary" size="lg" onPress={handleAdd} isDisabled={totalSelected === 0}>
            Use Selected
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
